// Auditoría de Saldos: lista de todo el inventario de Aplicaciones con
// entradas, salidas, el saldo que resultaría de entradas-salidas, y el
// saldo guardado — para vigilar que no se vuelvan a desalinear (tras la
// reconciliación hecha en Depuración de Inventario, no debería haber
// ninguna diferencia).
//
// Acceso: mismo permiso que el Catálogo de Productos (perm_ver_catalogo o perm_vob).

import React, { useEffect, useState } from 'react';
import { ClipboardCheck, Loader2, Search, CheckCircle2, AlertTriangle } from 'lucide-react';
import { supabase } from '../../supabase';
import { buildApiUrl } from '../../utils/apiUrl';
import { useEmpresaId } from '../../context/EmpresaContext';
import { useAppPermisos } from '../../hooks/useAppPermisos';
import { pageTitleCls, pageSubtitleCls, inputCls } from '../../components/ui';
import { SortableTh, sortRows, useTableSort } from '../../utils/tableSort';

type AuditoriaSortKey = 'codigo' | 'descripcion' | 'saldo_inicial' | 'entradas' | 'salidas' | 'saldo_final_calculado' | 'saldo_actual' | 'diferencia';

interface FilaAuditoria {
  id: number;
  codigo: string;
  descripcion: string;
  unidad_medida: string | null;
  activo: boolean;
  pertenece_aplicaciones: boolean;
  saldo_inicial: number;
  entradas: number;
  salidas: number;
  saldo_final_calculado: number;
  saldo_actual: number;
  diferencia: number;
  tiene_diferencia: boolean;
}

function fmt(n: number): string {
  return n.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

export function AuditoriaSaldosPage() {
  const empresaId = useEmpresaId();
  const { perm_vob, perm_ver_catalogo } = useAppPermisos();
  const puedeVer = perm_vob || perm_ver_catalogo;

  const [filas, setFilas] = useState<FilaAuditoria[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [soloConDiferencia, setSoloConDiferencia] = useState(false);
  const [soloAplicaciones, setSoloAplicaciones] = useState(true);
  const sort = useTableSort<AuditoriaSortKey>({ key: 'diferencia', direction: 'desc' });

  useEffect(() => {
    if (!puedeVer) return;
    let vivo = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const resp = await fetch(buildApiUrl('/aplicaciones/catalogo/auditoria-saldos'), {
          headers: {
            'x-empresa-id': String(empresaId),
            ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
          },
        });
        const j = await resp.json().catch(() => ({}));
        if (!vivo) return;
        if (!resp.ok) throw new Error(j?.error || 'No se pudo cargar la auditoría');
        setFilas(j.filas || []);
      } catch (e: any) {
        if (vivo) setError(e?.message || 'Error al cargar');
      } finally {
        if (vivo) setLoading(false);
      }
    })();
    return () => { vivo = false; };
  }, [empresaId, puedeVer]);

  if (!puedeVer) {
    return (
      <div className="p-6">
        <h1 className={pageTitleCls}>Auditoría de Saldos</h1>
        <p className={pageSubtitleCls}>Esta pantalla requiere acceso al catálogo de productos.</p>
      </div>
    );
  }

  const term = busqueda.trim().toLowerCase();
  const filtradas = sortRows(
    filas.filter(f => {
      if (soloConDiferencia && !f.tiene_diferencia) return false;
      if (soloAplicaciones && !f.pertenece_aplicaciones) return false;
      if (term && !f.codigo.toLowerCase().includes(term) && !f.descripcion.toLowerCase().includes(term)) return false;
      return true;
    }),
    sort.value,
    (row, key) => row[key],
  );
  const totalConDiferencia = filas.filter(f => f.tiene_diferencia).length;

  return (
    <div className="p-6 w-full">
      <style>{`
        .auditoria-tabla tbody tr:nth-child(even) td { background: var(--surface-overlay); }
        .auditoria-tabla tbody tr:nth-child(odd) td { background: transparent; }
        .auditoria-tabla tbody tr:hover td { background: var(--surface-raised); }
      `}</style>
      <div className="flex items-center gap-2 mb-1">
        <ClipboardCheck size={18} style={{ color: 'var(--emp-accent-txt)' }} />
        <h1 className={pageTitleCls}>Auditoría de Saldos</h1>
      </div>
      <p className={pageSubtitleCls}>
        Todo el inventario en el orden natural de un kardex: saldo inicial + entradas − salidas = saldo final,
        comparado contra el saldo guardado. La regla es que la diferencia sea siempre cero.
      </p>

      {!loading && (
        <div className="mt-4 mb-3 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--ink-faint)' }} />
            <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Buscar producto o código…"
              className={inputCls} style={{ paddingLeft: '30px' }} />
          </div>
          <label className="flex items-center gap-1.5 text-xs cursor-pointer" style={{ color: 'var(--ink-muted)' }}>
            <input type="checkbox" checked={soloConDiferencia} onChange={e => setSoloConDiferencia(e.target.checked)} />
            Solo con diferencia
          </label>
          <label className="flex items-center gap-1.5 text-xs cursor-pointer" style={{ color: 'var(--ink-muted)' }}>
            <input type="checkbox" checked={soloAplicaciones} onChange={e => setSoloAplicaciones(e.target.checked)} />
            Solo Aplicaciones
          </label>
          <span className="text-xs" style={{ color: totalConDiferencia > 0 ? 'var(--tone-warn-text)' : 'var(--tone-success-text)' }}>
            {totalConDiferencia === 0
              ? <><CheckCircle2 size={12} className="inline mr-1 -mt-0.5" />Todo cuadra ({filas.length} productos)</>
              : <><AlertTriangle size={12} className="inline mr-1 -mt-0.5" />{totalConDiferencia} con diferencia de {filas.length}</>}
          </span>
        </div>
      )}

      {error && <p className="text-sm" style={{ color: 'var(--tone-warn-text)' }}>{error}</p>}

      {loading ? (
        <div className="mt-8 flex items-center gap-2 text-sm" style={{ color: 'var(--ink-faint)' }}>
          <Loader2 size={16} className="animate-spin" /> Cargando…
        </div>
      ) : (
        <div className="w-full overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--line)' }}>
          <table className="auditoria-tabla w-full text-sm" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--surface-raised)' }}>
                <th className="px-3 py-2 font-semibold text-[10.5px] uppercase tracking-wider whitespace-nowrap" style={{ color: 'var(--ink-faint)' }}><SortableTh sort={sort} sortKey="codigo" align="center">Código</SortableTh></th>
                <th className="px-3 py-2 font-semibold text-[10.5px] uppercase tracking-wider whitespace-nowrap" style={{ color: 'var(--ink-faint)' }}><SortableTh sort={sort} sortKey="descripcion">Descripción</SortableTh></th>
                <th className="px-3 py-2 font-semibold text-[10.5px] uppercase tracking-wider whitespace-nowrap" style={{ color: 'var(--ink-faint)' }}><SortableTh sort={sort} sortKey="saldo_inicial" align="right">Saldo inicial</SortableTh></th>
                <th className="px-3 py-2 font-semibold text-[10.5px] uppercase tracking-wider whitespace-nowrap" style={{ color: 'var(--ink-faint)' }}><SortableTh sort={sort} sortKey="entradas" align="center">Entradas</SortableTh></th>
                <th className="px-3 py-2 font-semibold text-[10.5px] uppercase tracking-wider whitespace-nowrap" style={{ color: 'var(--ink-faint)' }}><SortableTh sort={sort} sortKey="salidas" align="center">Salidas</SortableTh></th>
                <th className="px-3 py-2 font-semibold text-[10.5px] uppercase tracking-wider whitespace-nowrap" style={{ color: 'var(--ink-faint)' }}><SortableTh sort={sort} sortKey="saldo_final_calculado" align="right">Saldo final (calculado)</SortableTh></th>
                <th className="px-3 py-2 font-semibold text-[10.5px] uppercase tracking-wider whitespace-nowrap" style={{ color: 'var(--ink-faint)' }}><SortableTh sort={sort} sortKey="saldo_actual" align="right">Saldo guardado</SortableTh></th>
                <th className="px-3 py-2 font-semibold text-[10.5px] uppercase tracking-wider whitespace-nowrap" style={{ color: 'var(--ink-faint)' }}><SortableTh sort={sort} sortKey="diferencia" align="right">Diferencia</SortableTh></th>
              </tr>
            </thead>
            <tbody>
              {filtradas.map((f) => (
                <tr key={f.id} style={{ opacity: f.activo ? 1 : 0.55 }}>
                  <td className="px-3 py-1.5 text-center font-mono text-xs">{f.codigo}</td>
                  <td className="px-3 py-1.5" style={{ color: 'var(--ink)' }}>{f.descripcion}</td>
                  <td className="px-3 py-1.5 text-right font-mono" style={{ color: 'var(--ink-faint)' }}>{fmt(f.saldo_inicial)}</td>
                  <td className="px-3 py-1.5 text-center" style={{ color: 'var(--ink-muted)' }}>{f.entradas}</td>
                  <td className="px-3 py-1.5 text-center" style={{ color: 'var(--ink-muted)' }}>{f.salidas}</td>
                  <td className="px-3 py-1.5 text-right font-mono" style={{ color: 'var(--ink-muted)' }}>{fmt(f.saldo_final_calculado)}</td>
                  <td className="px-3 py-1.5 text-right font-mono" style={{ color: 'var(--ink)' }}>{fmt(f.saldo_actual)}</td>
                  <td className="px-3 py-1.5 text-right font-mono font-semibold" style={{ color: f.tiene_diferencia ? 'var(--tone-warn-text)' : 'var(--ink-faint)' }}>
                    {f.tiene_diferencia ? (f.diferencia > 0 ? '+' : '') + fmt(f.diferencia) : '—'}
                  </td>
                </tr>
              ))}
              {filtradas.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-sm" style={{ color: 'var(--ink-faint)' }}>Sin resultados.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
